import { zonedDateTime } from '@/lib/reporting/timezone';
import type {
  BookingTiming,
  IntervalUnit,
  TreatmentCourseTemplateDraft,
} from '@/types/treatmentCourses';
import { safeLocalDateTime } from '@/utils/appointmentCivilDate';
import { appointmentEndForService } from '@/utils/calendarTimeBlock';

export type WizardPhase =
  | { type: 'patient' }
  | { type: 'template' }
  | { type: 'step'; index: number }
  | { type: 'review' };

export type DraftStepBooking = {
  templateStepId: string;
  sortOrder: number;
  unbooked: boolean;
  startsAt: string | null;
  endsAt: string | null;
  durationMinutes: number;
  practitionerIds: string[];
  resourceIds: string[];
  /** Slot list vs the date/start/end fields. Absent until a time is chosen. */
  timeSource?: 'slot' | 'manual';
};

export type HeldDraftInterval = {
  starts_at: string;
  ends_at: string;
  practitioner_ids: string[];
  resource_ids: string[];
};

export function intervalToDays(value: number, unit: IntervalUnit): number {
  const n = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  switch (unit) {
    case 'days':
      return n;
    case 'weeks':
      return n * 7;
    default: {
      const _exhaustive: never = unit;
      return _exhaustive;
    }
  }
}

export function canLeaveStepUnbooked(bookingTiming: BookingTiming, stepIndex: number): boolean {
  return bookingTiming === 'allow_unbooked' && stepIndex > 0;
}

/** «Opret uden aftale» — only templates that already allow later steps to stay unbooked. */
export function canOfferCreateWithoutAppointments(bookingTiming: BookingTiming): boolean {
  return bookingTiming === 'allow_unbooked';
}

export function showCreateWithoutAppointmentButton(
  phase: WizardPhase,
  bookingTiming: BookingTiming,
): boolean {
  return (
    phase.type === 'step' &&
    phase.index === 0 &&
    canOfferCreateWithoutAppointments(bookingTiming)
  );
}

/** Every step left unbooked on a template that allows it. Step 1 included. */
export function courseDraftWithoutAppointments(input: {
  patientId: string | null;
  template: TreatmentCourseTemplateDraft | null;
  bookings: DraftStepBooking[];
}): boolean {
  if (!input.patientId || !input.template) return false;
  if (!canOfferCreateWithoutAppointments(input.template.booking_timing)) return false;
  if (input.template.steps.length === 0) return false;
  if (input.bookings.length !== input.template.steps.length) return false;
  return input.bookings.every((booking) => booking.unbooked);
}

export function wizardPhases(stepCount: number): WizardPhase[] {
  const phases: WizardPhase[] = [{ type: 'patient' }, { type: 'template' }];
  for (let index = 0; index < stepCount; index += 1) {
    phases.push({ type: 'step', index });
  }
  phases.push({ type: 'review' });
  return phases;
}

export function phaseIndex(phases: WizardPhase[], current: WizardPhase): number {
  return phases.findIndex((phase) => {
    if (phase.type !== current.type) return false;
    if (phase.type === 'step' && current.type === 'step') return phase.index === current.index;
    return true;
  });
}

export function nextPhase(phases: WizardPhase[], current: WizardPhase): WizardPhase | null {
  const index = phaseIndex(phases, current);
  if (index < 0 || index >= phases.length - 1) return null;
  return phases[index + 1] ?? null;
}

export function previousPhase(phases: WizardPhase[], current: WizardPhase): WizardPhase | null {
  const index = phaseIndex(phases, current);
  if (index <= 0) return null;
  return phases[index - 1] ?? null;
}

export function defaultStepBookings(template: TreatmentCourseTemplateDraft): DraftStepBooking[] {
  return template.steps.map((step, index) => ({
    templateStepId: step.clientId,
    sortOrder: index + 1,
    unbooked: false,
    startsAt: null,
    endsAt: null,
    durationMinutes: step.duration_minutes ?? 0,
    practitionerIds: [],
    resourceIds: [],
  }));
}

/** Instant compare — same as SQL `starts_at < now()` (Europe/Copenhagen grid). */
export function slotStartIsInFuture(
  iso: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!iso) return false;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return false;
  return parsed >= nowMs;
}

export function futureSlots<T extends { starts_at: string }>(
  slots: T[],
  nowMs: number = Date.now(),
): T[] {
  return slots.filter((slot) => slotStartIsInFuture(slot.starts_at, nowMs));
}

export const SLOT_SEARCH_NOW_ROUND_MINUTES = 5;
export const SLOT_SEARCH_FALLBACK_DAYS = 13;

export function roundNowMs(
  nowMs: number = Date.now(),
  minutes: number = SLOT_SEARCH_NOW_ROUND_MINUTES,
): number {
  const step = Math.max(1, Math.trunc(minutes)) * 60_000;
  return Math.floor(nowMs / step) * step;
}

export function civilDateFromInstant(atMs: number): string {
  const date = new Date(atMs);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function civilDateFromIso(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;
  return civilDateFromInstant(parsed);
}

export function addCivilDays(civil: string, days: number): string {
  const date = new Date(`${civil}T12:00:00`);
  if (Number.isNaN(date.getTime())) return civil;
  date.setDate(date.getDate() + days);
  return civilDateFromInstant(date.getTime());
}

export function localDayStartIso(civil: string): string {
  return new Date(`${civil}T00:00:00`).toISOString();
}

export function localDayEndIso(civil: string): string {
  return new Date(`${civil}T23:59:59`).toISOString();
}

export function clampIsoToNow(iso: string | null, nowMs: number = Date.now()): string | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;
  return parsed < nowMs ? new Date(nowMs).toISOString() : iso;
}

/**
 * Default date-picker range for an unbooked course step.
 * Previous booked step + interval ± tolerance (e.g. 4.11 + 14d ± 3d → 15.–21.11).
 * Start is never before today. Without a previous start or interval: today + 13 days.
 */
export function defaultSlotSearchCivilRange(input: {
  previousStartsAt?: string | null;
  intervalValue?: number | null;
  intervalUnit?: IntervalUnit;
  toleranceValue?: number | null;
  toleranceUnit?: IntervalUnit;
  nowMs?: number;
  fallbackDays?: number;
}): { rangeStart: string; rangeEnd: string } {
  const nowMs = input.nowMs ?? Date.now();
  const fallbackDays = input.fallbackDays ?? SLOT_SEARCH_FALLBACK_DAYS;
  const today = civilDateFromInstant(nowMs);
  const previousCivil = civilDateFromIso(input.previousStartsAt ?? null);
  const intervalDays = intervalToDays(input.intervalValue ?? 0, input.intervalUnit ?? 'days');
  if (!previousCivil || intervalDays <= 0) {
    return { rangeStart: today, rangeEnd: addCivilDays(today, fallbackDays) };
  }
  const toleranceDays = intervalToDays(
    input.toleranceValue ?? 0,
    input.toleranceUnit ?? 'days',
  );
  const center = addCivilDays(previousCivil, intervalDays);
  let rangeStart = addCivilDays(center, -toleranceDays);
  let rangeEnd = addCivilDays(center, toleranceDays);
  if (rangeStart < today) rangeStart = today;
  if (rangeEnd < rangeStart) rangeEnd = addCivilDays(rangeStart, fallbackDays);
  return { rangeStart, rangeEnd };
}

/** Date-picker range: booked start if present, otherwise the interval window. */
export function initialSlotSearchCivilRange(input: {
  bookedStartsAt?: string | null;
  previousStartsAt?: string | null;
  intervalValue?: number | null;
  intervalUnit?: IntervalUnit;
  toleranceValue?: number | null;
  toleranceUnit?: IntervalUnit;
  nowMs?: number;
  fallbackDays?: number;
}): { rangeStart: string; rangeEnd: string } {
  const bookedCivil = civilDateFromIso(input.bookedStartsAt ?? null);
  if (bookedCivil) {
    const fallbackDays = input.fallbackDays ?? SLOT_SEARCH_FALLBACK_DAYS;
    return { rangeStart: bookedCivil, rangeEnd: addCivilDays(bookedCivil, fallbackDays) };
  }
  return defaultSlotSearchCivilRange(input);
}

export function searchWindowForStep(input: {
  stepIndex: number;
  step1StartIso: string;
  step1EndIso: string;
  previousStartsAt: string | null;
  intervalValue: number;
  intervalUnit: IntervalUnit;
  toleranceValue: number;
  toleranceUnit: IntervalUnit;
}): { windowStart: string | null; windowEnd: string | null; previousStartsAt: string | null } {
  if (input.stepIndex <= 0) {
    return {
      windowStart: input.step1StartIso,
      windowEnd: input.step1EndIso,
      previousStartsAt: null,
    };
  }
  if (!input.previousStartsAt) {
    return { windowStart: null, windowEnd: null, previousStartsAt: null };
  }
  return {
    windowStart: null,
    windowEnd: null,
    previousStartsAt: input.previousStartsAt,
  };
}

export function heldIntervalsFromBookings(bookings: DraftStepBooking[]): HeldDraftInterval[] {
  return bookings
    .filter((row) => !row.unbooked && row.startsAt && row.endsAt && row.practitionerIds.length > 0)
    .map((row) => ({
      starts_at: row.startsAt as string,
      ends_at: row.endsAt as string,
      practitioner_ids: row.practitionerIds,
      resource_ids: row.resourceIds,
    }));
}

export function applySlotToBooking(
  booking: DraftStepBooking,
  slot: {
    starts_at: string;
    ends_at: string;
    duration_minutes: number;
    practitioner_ids: string[];
    resource_ids: string[];
  },
): DraftStepBooking {
  return {
    ...booking,
    unbooked: false,
    startsAt: slot.starts_at,
    endsAt: slot.ends_at,
    durationMinutes: slot.duration_minutes,
    practitionerIds: slot.practitioner_ids,
    resourceIds: slot.resource_ids,
    timeSource: 'slot',
  };
}

/** Europe/Copenhagen wall clock for the course step date/start/end fields. */
export function clinicWallFromIso(iso: string | null | undefined): {
  civil: string;
  clock: string;
} {
  if (!iso) return { civil: '', clock: '' };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { civil: '', clock: '' };
  const parts = zonedDateTime(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    civil: `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`,
    clock: `${pad(parts.hour)}:${pad(parts.minute)}`,
  };
}

/** End clock after applying the step duration, including a midnight crossing. */
export function courseStepEndClock(
  civil: string,
  startClock: string,
  durationMinutes: number,
): string | null {
  const start = safeLocalDateTime(`${civil}T${startClock}`);
  if (!start) return null;
  const parts = zonedDateTime(appointmentEndForService(start, durationMinutes));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(parts.hour)}:${pad(parts.minute)}`;
}

/**
 * Manual date/start/end, same rules as Ny aftale: start follows the step
 * duration unless an explicit end clock is passed. An empty practitioner
 * list keeps whoever a slot already assigned. Resources are cleared because
 * this time is not a searched free slot; booking auto-assigns them.
 */
export function applyManualCourseStepTime(
  booking: DraftStepBooking,
  input: {
    civil: string;
    startClock: string;
    endClock?: string;
    durationMinutes?: number;
    practitionerIds: readonly string[];
  },
): DraftStepBooking {
  const start = safeLocalDateTime(`${input.civil}T${input.startClock}`);
  if (!start) return booking;
  const end = input.endClock
    ? safeLocalDateTime(`${input.civil}T${input.endClock}`)
    : input.durationMinutes != null
      ? appointmentEndForService(start, input.durationMinutes)
      : null;
  if (!end) return booking;
  const practitionerIds =
    input.practitionerIds.length > 0 ? [...input.practitionerIds] : booking.practitionerIds;
  return {
    ...booking,
    unbooked: false,
    startsAt: start.toISOString(),
    endsAt: end.toISOString(),
    practitionerIds,
    resourceIds: [],
    timeSource: 'manual',
  };
}

export function markStepUnbooked(booking: DraftStepBooking): DraftStepBooking {
  return {
    ...booking,
    unbooked: true,
    startsAt: null,
    endsAt: null,
    practitionerIds: [],
    resourceIds: [],
    timeSource: undefined,
  };
}

export function markAllStepsUnbooked(bookings: readonly DraftStepBooking[]): DraftStepBooking[] {
  return bookings.map(markStepUnbooked);
}

export function stepBookingComplete(
  booking: DraftStepBooking,
  bookingTiming: BookingTiming,
  stepIndex: number,
): boolean {
  if (booking.unbooked) return canLeaveStepUnbooked(bookingTiming, stepIndex);
  return Boolean(
    booking.startsAt &&
    booking.endsAt &&
    Date.parse(booking.endsAt) > Date.parse(booking.startsAt) &&
    booking.practitionerIds.length > 0 &&
    slotStartIsInFuture(booking.startsAt),
  );
}

export function canAdvanceFromPhase(input: {
  phase: WizardPhase;
  patientId: string | null;
  template: TreatmentCourseTemplateDraft | null;
  bookings: DraftStepBooking[];
}): boolean {
  switch (input.phase.type) {
    case 'patient':
      return Boolean(input.patientId);
    case 'template':
      return Boolean(input.template && input.template.steps.length > 0);
    case 'step': {
      if (!input.template) return false;
      const booking = input.bookings[input.phase.index];
      if (!booking) return false;
      return stepBookingComplete(booking, input.template.booking_timing, input.phase.index);
    }
    case 'review':
      return false;
    default: {
      const _exhaustive: never = input.phase;
      return _exhaustive;
    }
  }
}

export function courseDraftReadyToBook(input: {
  patientId: string | null;
  template: TreatmentCourseTemplateDraft | null;
  bookings: DraftStepBooking[];
}): boolean {
  if (courseDraftWithoutAppointments(input)) return true;
  if (!input.patientId || !input.template) return false;
  if (input.bookings.length !== input.template.steps.length) return false;
  return input.bookings.every((booking, index) =>
    stepBookingComplete(booking, input.template!.booking_timing, index),
  );
}

export function bookTreatmentCoursePayload(input: {
  template: TreatmentCourseTemplateDraft;
  bookings: DraftStepBooking[];
}): Array<{
  template_step_id: string;
  starts_at: string | null;
  ends_at: string | null;
  practitioner_ids: string[];
  resource_ids: string[];
}> {
  return input.template.steps.map((step, index) => {
    const booking = input.bookings[index];
    return {
      template_step_id: step.clientId,
      starts_at: booking && !booking.unbooked ? booking.startsAt : null,
      ends_at: booking && !booking.unbooked ? booking.endsAt : null,
      practitioner_ids: booking && !booking.unbooked ? booking.practitionerIds : [],
      resource_ids: booking && !booking.unbooked ? booking.resourceIds : [],
    };
  });
}
